import { useCallback, useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useAppContext } from "../../../providers/ProviderAppContainer";
import { Button } from "primereact/button";
import TabHeader from "../../common/TabHeader";
import { Divider } from "primereact/divider";
import Loading from "../../common/Loading";
import NoContent from "../../common/NoContent";
import HasRequiredAuthority from "../../dependencies/HasRequiredAuthority";
import { AUTHORITIES } from "../../../constants";
import { getReadableDate } from "../../../utils";
import { TEXT_NORMAL, TEXT_SMALL } from "../../../style";
import DialogAddUserToBatch from "./DialogAddUserToBatch";

export default function UserBatches() {
    const { userId } = useOutletContext();
    const navigate = useNavigate();
    const { requestAPI } = useAppContext();

    const [loading, setLoading] = useState();
    const [error, setError] = useState();
    const [batches, setBatches] = useState();
    const [dialogAddToBatch, setDialogAddToBatch] = useState({ visible: false });

    const closeDialogAddToBatch = useCallback(() => {
        setDialogAddToBatch((prev) => ({ ...prev, visible: false }));
    }, []);

    useEffect(() => {
        requestAPI({
            requestPath: `users/${userId}/batches`,
            requestMethod: "GET",
            setLoading,
            onRequestFailure: setError,
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200) {
                    setBatches(data);
                } else {
                    setError("Couldn't load Batches");
                }
            },
        });
    }, [requestAPI, userId]);

    return (
        <div className="flex flex-column h-full">
            <TabHeader
                className="px-3 pt-3"
                title="User Batches"
                highlights={["Batches this user is assigned to"]}
                actionItems={[
                    <HasRequiredAuthority requiredAuthority={AUTHORITIES.ASSIGN_BATCH_STUDENT}>
                        <Button
                            icon="pi pi-plus"
                            severity="warning"
                            onClick={() =>
                                setDialogAddToBatch({
                                    visible: true,
                                    userId,
                                    setBatches,
                                    closeDialog: closeDialogAddToBatch,
                                })
                            }
                        />
                    </HasRequiredAuthority>,
                ]}
            />
            <Divider />

            {loading ? (
                <Loading />
            ) : error ? (
                <NoContent error={error} />
            ) : (
                <div className="flex-1 overflow-y-scroll p-2 flex flex-column gap-2">
                    {batches?.length ? (
                        batches.map((batch) => (
                            <div
                                key={batch.id}
                                className={`border-1 border-gray-300 border-round py-2 px-3 text-white ${batch.active ? "bg-green-500" : "bg-red-500"}`}
                            >
                                <div className="flex align-items-start justify-content-between gap-2">
                                    <div className="flex-1 overflow-hidden">
                                        <div className={`${TEXT_NORMAL} font-semibold`}>
                                            {batch.id}. {batch.title}
                                        </div>
                                        <div className={`${TEXT_SMALL} mt-1`}>
                                            {batch.branch_title || "No branch"} · {getReadableDate({ date: batch.start_date, removeTime: true })} –{" "}
                                            {getReadableDate({ date: batch.end_date, removeTime: true })}
                                        </div>
                                        {batch.roll_no != null ? <div className={`${TEXT_SMALL} mt-1`}>Roll {batch.roll_no}</div> : null}
                                        {batch.assigned_on ? (
                                            <div className={`${TEXT_SMALL} mt-1`}>Assigned {getReadableDate({ date: batch.assigned_on })}</div>
                                        ) : null}
                                    </div>
                                    <Button
                                        label="Attendance"
                                        icon="pi pi-calendar"
                                        size="small"
                                        severity="help"
                                        onClick={() => navigate(`/manage-users/${userId}/batches/${batch.id}/attendance`)}
                                    />
                                </div>
                            </div>
                        ))
                    ) : (
                        <NoContent error="No Batches Assigned" />
                    )}
                </div>
            )}

            {dialogAddToBatch?.visible && <DialogAddUserToBatch {...dialogAddToBatch} />}
        </div>
    );
}
